import React, {useContext, useState, useEffect} from 'react';
import {Route, withRouter} from 'react-router-dom';
import {LoopContext, LoopMetadata} from 'components/forms/context';
import {useFormPersistence} from '../hooks';
import {FormState} from 'components/forms/context';
import {BranchGroup} from 'components/forms/navigation';
import {findBranch} from 'components/forms/utils/branch';
import get from 'lodash.get';
import set from 'lodash.set';

const Loop = ({history, node, children}) => {
    const {state, updateState} = useFormPersistence();
    const {updateState: deleteStateNode} = useFormPersistence(null, false);

    const {setLoopNavigators, setNavigationBookmark} = useContext(FormState);
    const looper = useContext(LoopContext);
    const {data: metaData, updateData} = useContext(LoopMetadata);

    const nodePath = looper ? `${looper.node}[${looper.index}].${node}` : node;
    const parentNodePath = looper
        ? `${looper.node}[${looper.index}]`
        : undefined;
    const [index, setIndexState] = useState(
        get(state, nodePath, []).length - 1
    );
    const loopNode = node ? `${nodePath}[${index}]` : '';

    // Reset child index to last item if parent index changes
    useEffect(() => {
        setIndexState(get(state, nodePath, []).length - 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodePath]);

    // Update metadata on index change
    useEffect(() => {
        updateData({
            [node]: index,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node, index]);

    // Push navigators to form
    useEffect(() => {
        setLoopNavigators &&
            setLoopNavigators(node, {
                goTo,
                setIndex,
                deleteChild,
                remove,
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node]);

    /**
     * Set current index to specified value.
     * Form state should pull existing values automatically.
     * @param {integer} index index of item to make active
     */
    const setIndex = childIndex => {
        setIndexState(childIndex);
    };

    /**
     * Append a new instance into state and set current
     * working index to the newest
     */
    const addNew = () => {
        // Update state now with new object for new index
        // This avoids requiring the form to create this so
        // looping can work in other contexts like testing,
        // outside of a form.
        updateState(set(state, `${nodePath}[${index + 1}]`, {}));
        setIndex(get(state, nodePath).length - 1);
    };

    const deleteChild = (childIndex, parentName, parentIndex) => {
        const deletePath = parentName
            ? `${parentName}[${parentIndex}].${node}`
            : nodePath;
        const oldItems = get(state, deletePath, []);
        const items = [
            ...oldItems.slice(0, childIndex),
            ...oldItems.slice(childIndex + 1),
        ];
        deleteStateNode(
            set(
                {
                    ...state,
                },
                deletePath,
                items
            )
        );
    };

    /**
     * Remove the item at specified index from state persistence
     * @param {iteger} index item to remove
     */
    const remove = childIndex => {
        deleteChild(childIndex);
        setIndex(childIndex - 1);
    };

    /**
     * Go to section at specified index. Defaults to index 0.
     * Useful for jumping to different known loop points
     * @param {integer} index index of page to navigate to
     */
    const getSections = children => {
        // copy-pasting from ProgressForm.jsx... some of this functionality is unneeded here
        let decisionState = state;

        return React.Children.toArray(children).reduce((children, child) => {
            if (child.type === BranchGroup) {
                const validBranch = React.Children.toArray(
                    child.props.children
                ).find(branch => findBranch(branch, metaData, decisionState));
                return [
                    ...children,
                    ...getSections(validBranch?.props.children),
                ];
            }
            if (child.type === Loop) {
                return [...children, ...getSections(child.props.children)];
            }
            if (child.type === Route) {
                return [...children, child];
            }

            return children;
        }, []);
    };

    const goTo = (childIndex = 0, backPath) => {
        const flatChildren = getSections(children);
        const nextPath = get(flatChildren, `[${childIndex}].props.path`);

        // If we were passed a path, set a ProgressForm context bookmark
        if (nextPath) {
            if (backPath) {
                setNavigationBookmark([
                    {
                        origin: window.location.pathname,
                        direction: -1,
                        path: nextPath,
                    },
                ]);
            }
            history.push(nextPath);
        }
    };

    // Ensure there is always a state to write to
    if (index === -1) {
        addNew();
    }

    return (
        <LoopContext.Provider
            value={{
                node,
                nodePath,
                loopNode,
                parentNodePath,
                parent: looper,
                index,
                setIndex,
                addNew,
                remove,
                goTo,
            }}
        >
            {children}
        </LoopContext.Provider>
    );
};

export default withRouter(Loop);
